import { ActionType, CompleteAvailabilityStepPayload, RecurringIntervalSelected, ReservationCreated, ResourceAfterAvailability } from "../../AppDataProvider/actions";
import { AppContext, BookingData, RecurringTimeSlot, ServiceDuration, TimeSlot } from "../../AppDataProvider/AppDataProvider";
import { CreateReservationMutationVariables, ResourceAllocationType, ResourceSelectionMode, useCreateReservationMutation } from "../../GeneratedGraphQL";
import { emitEvent, getReservationPartsInput, requireBookingDataFields } from "../../utils";
import React, { ReactElement, useContext, useState } from "react";
import { CalendarViewData } from "./CalendarView/CalendarViewData/CalendarViewData";
import { faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";
import { isEqual } from "date-fns";
import { ListView } from "./ListView/ListView";
import { PartRequired } from "../../utils/types";
import styled from "styled-components";
import { useApolloClient } from "@apollo/client";
import { useDeleteExistingReservedSpace } from "./hooks/useDeleteExistingReservedSpace";
import { useTranslation } from "react-i18next";
type AvailabilityStepBookingData = PartRequired<BookingData, "createdSource" | "organisation" | "service" | "timezone">;
const isRecurringTimeSlotCheck = (selectedTimeSlot: TimeSlot): selectedTimeSlot is RecurringTimeSlot => {
  const recurringTimeSlot = (selectedTimeSlot as RecurringTimeSlot);
  return recurringTimeSlot.interval.__typename === "GroupBookingInterval" && recurringTimeSlot.interval.booking.recurring !== undefined;
};
export function AvailabilityStep(): ReactElement {
  const {
    t
  } = useTranslation();
  const {
    appDataDispatch,
    appData
  } = useContext(AppContext);
  const [createReservation] = useCreateReservationMutation({
    errorPolicy: "all",
    onCompleted(data) {
      if (data?.createReservation) {
        emitEvent({
          data: {
            iFrameId: appData.iFrameId ?? "",
            organisationId: appData.bookingData.organisation?._id ?? "",
            reservationSlotId: data.createReservation.reservedSpace._id,
            selectedTimeSlot: data.createReservation.reservation.start
          },
          name: "BOOKING_SLOT_RESERVED"
        });
      }
    }
  });
  const client = useApolloClient();
  requireBookingDataFields<AvailabilityStepBookingData>(appData.bookingData, ["createdSource", "service", "timezone", "organisation"]);
  const {
    createdSource,
    organisation,
    reservedSpaceId,
    resource,
    resourceGroupId,
    service,
    startDate,
    timezone
  } = appData.bookingData;
  const {
    parts
  } = service;
  const {
    _id,
    resourceSelectionMode
  } = appData.bookingTool;

  // If no reserved space exists, this function does nothing, so it can be
  // safely called unconditionally.
  const deleteExistingReservedSpace = useDeleteExistingReservedSpace({
    organisationId: organisation._id,
    reservedSpaceId
  });
  const [createReservationLoading, setCreateReservationLoading] = useState<boolean>(false);
  const goBack = () => {
    void deleteExistingReservedSpace();

    /**
     * If we return to the availability step we do not
     * want to retain any of the cached results
     * and we do not want to refetch the cached queries.
     */
    void client.clearStore().then(() => {
      appDataDispatch({
        type: ActionType.BACK_FROM_AVAILABILITY_STEP
      });
    });
  };
  type CreateStandardReservationOpts = {
    attendees: number;
    duration: ServiceDuration;
    selectedTimeSlot: TimeSlot;
  };
  const createStandardReservation = async (options: CreateStandardReservationOpts) => {
    const {
      selectedTimeSlot,
      attendees,
      duration
    } = options;
    const {
      interval
    } = selectedTimeSlot;

    /**
     * Add 1 minute to reservation expiry on the back end.
     * This buffer means front-end will prompt for a reload 1 minute before
     * actual expiry, avoiding situations where user is interacting at same
     * time as reservation expiry.
     * Chronos only accepts full-minute values so 1 minute is easiest compromise
     */
    const {
      reservationExpiryMinutes
    } = organisation;
    const expiresAfter = reservationExpiryMinutes + 1;
    const variables: CreateReservationMutationVariables = {
      bookingToolId: _id !== "GENERIC_BOOKING_TOOL" ? _id : undefined,
      expiresAfter,
      ...(interval.__typename === "AvailableInterval" && {
        intervalResourceIds: interval.resourceIds
      }),
      parts: getReservationPartsInput({
        parts,
        selectedInterval: interval
      }),
      requiredSpaces: attendees,
      ...(resource && {
        resourceId: resource?._id
      }),
      ...(resourceGroupId && {
        resourceGroupId
      }),
      serviceId: service._id,
      source: createdSource,
      timeZone: timezone
    };
    const {
      data,
      errors
    } = await createReservation({
      variables
    });
    if (errors?.some(error => error.message === "Response code 422 (Unprocessable Entity)")) {
      appDataDispatch({
        payload: {
          buttonLabel: t("reload"),
          content: t("errors.somethingChanged"),
          icon: faExclamationTriangle,
          onClick: /* istanbul ignore next */() => location.reload(),
          title: t("somethingChanged")
        },
        type: ActionType.SHOW_MODAL_DIALOG
      });
      return;
    } else if (!data || errors) {
      appDataDispatch({
        payload: {
          message: t("errors.cannotCreateReservation"),
          variant: "warning"
        },
        type: ActionType.ADD_TOAST
      });
      setCreateReservationLoading(false);
      return;
    }
    const {
      reservedSpace,
      reservation
    } = data.createReservation;

    // Wait until after confirming the new reservation exists before deleting
    // any existing reservation
    await deleteExistingReservedSpace();
    setCreateReservationLoading(false);
    const groupBooking = interval.__typename === "GroupBookingInterval" ? interval.booking : undefined;
    const resourceIds = interval.__typename === "AvailableInterval" ? interval.resourceIds : [];
    const updatedData: ReservationCreated = {
      attendees,
      duration,
      reservedSpaceId: reservedSpace._id,
      startDate: new Date(reservation.start),
      timezone,
      ...(resourceIds && {
        resourceIds: resourceIds
      }),
      ...(groupBooking && {
        selectedBookings: [groupBooking]
      }),
      ...(groupBooking && {
        selectedBookings: [groupBooking]
      })
    };
    const payload: CompleteAvailabilityStepPayload = {
      updatedData
    };
    appDataDispatch({
      payload,
      type: ActionType.COMPLETE_AVAILABILITY_STEP
    });

    // This is a special handler for those who want to reserve using our widget but then complete the booking at a later date
    // such as checkout
    if (appData.reservationOnly && appData.iFrameId) {
      emitEvent({
        data: {
          iFrameId: appData.iFrameId ?? "",
          organisationId: appData.bookingData.organisation?._id ?? "",
          reservationSlotId: updatedData.reservedSpaceId ?? "",
          selectedTimeSlot: updatedData.startDate ? updatedData.startDate.toISOString() : ""
        },
        name: "CLOSE_APPOINTEDD_POPOVER_RESERVED"
      });
      emitEvent({
        data: {
          iFrameId: appData.iFrameId ?? ""
        },
        name: "CLOSE_APPOINTEDD_POPOVER"
      });
    }
  };
  type CompleteStepOpts = {
    attendees: number;
    duration: ServiceDuration;
    selectedTimeSlot: TimeSlot;
  };
  const completeStep = (options: CompleteStepOpts) => {
    const {
      selectedTimeSlot,
      attendees,
      duration
    } = options;

    /**
     * If the user selects the same slot as the existing reservation, do not
     * create an additional reservation.
     */
    const userSelectedTheSameSlot = reservedSpaceId && startDate && isEqual(new Date(selectedTimeSlot.interval.start), startDate);
    if (userSelectedTheSameSlot) {
      appDataDispatch({
        type: ActionType.COMPLETE_AVAILABILITY_STEP
      });
      return;
    }
    setCreateReservationLoading(true);

    /**
     * If the selected time slot is for a recurring booking
     * we send the user to the recurring booking step where
     * they create a slot there and not here.
     */
    if (isRecurringTimeSlotCheck(selectedTimeSlot)) {
      const updatedData: RecurringIntervalSelected = {
        attendees,
        duration,
        recurringBooking: selectedTimeSlot.interval.booking,
        timezone
      };
      const payload: CompleteAvailabilityStepPayload = {
        updatedData
      };

      /**
       * If in the future we return to the availability step
       * we do not want to retain any of the cached results
       * and we do not want to refetch the cached queries.
       */
      void client.clearStore().then(() => {
        appDataDispatch({
          payload,
          type: ActionType.COMPLETE_AVAILABILITY_STEP
        });
      });
      return;
    }
    const resourceSelectionBeforeAvailability = organisation?.resourceSelectionBeforeAvailability;
    const groupBooking = selectedTimeSlot.interval.__typename === "GroupBookingInterval" ? selectedTimeSlot.interval.booking : undefined;

    /**
     * if resource selection is enabled and is after availability.
     * We need to store the selected slot then move
     * on to the resource selection step.
     *
     * Group bookings already have a resource selected,
     * making this flow redundant.
     */
    if (!groupBooking && !resourceSelectionBeforeAvailability && resourceSelectionMode !== ResourceSelectionMode.Disabled && service.resourceAllocationType === ResourceAllocationType.Any) {
      const updatedData: ResourceAfterAvailability = {
        attendees,
        duration,
        selectedTimeSlot: selectedTimeSlot,
        timezone
      };
      const payload: CompleteAvailabilityStepPayload = {
        updatedData
      };

      /**
       * If in the future we return to the availability step
       * we do not want to retain any of the cached results
       * and we do not want to refetch the cached queries.
       */
      void client.clearStore().then(() => {
        appDataDispatch({
          payload,
          type: ActionType.COMPLETE_AVAILABILITY_STEP
        });
      });
      return;
    }
    void createStandardReservation({
      attendees,
      duration,
      selectedTimeSlot
    });
  };
  const availabilityDisplayMode = service.availabilityDisplayMode;
  return <Container data-sentry-element="Container" data-sentry-component="AvailabilityStep" data-sentry-source-file="AvailabilityStep.tsx">
      {availabilityDisplayMode === "LIST" ? <ListView createReservationLoading={createReservationLoading} onCompleteStep={completeStep} onGoBack={goBack} /> : <CalendarViewData createReservationLoading={createReservationLoading} onCompleteStep={completeStep} onGoBack={goBack} />}
    </Container>;
}
export default AvailabilityStep;
const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-self: center;

  width: 100%;
`;